import { useCallback, useState } from 'react';

import {
  useSllDeleteGroupModuleContentMutation,
  useSllDeleteGroupModuleMutation,
  GetContentAllGroupsDocument,
  SllGroupModuleFragmentDoc,
  useSllCreateGroupModuleMutation,
  useSllUpdateGroupModuleMutation,
  useSllGetGroupModulesLazyQuery,
  useSllUpdateVideoGroupModuleMutation,
} from '@api/graphql-codegen';
import { DefaultModuleEnum, ExtendedSllModule } from '@types';
import { useEditLesson } from '../../useEditLesson';
import { logger } from '../../../../../utils/logger';

export interface useSchoolGroupsModuleCRUDProps {
  currentTabContent?: ExtendedSllModule;
  groups: ExtendedSllModule[];
  clearFile: () => void;
  activeTab: string;
  isGroupContent: boolean;
  onChangeData: (v: string | null) => void;
  onChangeGroupTabs: (v: ExtendedSllModule[]) => void;
  onChangeTab: (key: string) => void;
};

export const useSchoolGroupsModuleCRUD = ({ currentTabContent, groups, activeTab, clearFile, onChangeData, onChangeGroupTabs, onChangeTab }: useSchoolGroupsModuleCRUDProps) => {
  const { content } = useEditLesson();
  const [isOpenAddModal, setOpenAddModal] = useState<boolean>(false);

  const [getContentsByGroup, { loading: isFetchingAllGroups }] = useSllGetGroupModulesLazyQuery({ fetchPolicy: 'network-only' });
  const [deleteGroupModuleData, { loading: deletingGroupModuleData }] = useSllDeleteGroupModuleMutation({
    update(cache, res) {
      if (res.data?.sllDeleteGroupModule) {
        cache.updateFragment({
          id: `SLLGroupModule:{"moduleId":"${currentTabContent?.id}","groupId":"${currentTabContent?.groupId}"} `,
          fragment: SllGroupModuleFragmentDoc,
        }, (c) => ({ ...c, data: null }))
        onChangeData(null);
        const newGroups = [...groups];
        const index = newGroups.findIndex(g => g.groupId === currentTabContent?.groupId);
        newGroups[index].data = null;
        onChangeGroupTabs(newGroups);
      }
    }
  });

  const [deleteGroupContent, { loading: isGroupRemoving }] = useSllDeleteGroupModuleContentMutation({
    update(cache, res) {
      if (res.data?.sllDeleteGroupModuleContent) {
        const defaultContent = groups[0];
        let updatingGroups: any[] = groups.slice(1);
        const index = updatingGroups.findIndex(a => a.groupName === activeTab);
        if (index > -1) {
          updatingGroups.splice(index, 1);
        }
        cache.writeQuery({
          query: GetContentAllGroupsDocument,
          data: {
            getContentAllGroups: {
              __typename: "SLLExtendedModule",
              groups: updatingGroups
            }
          },
          variables: {
            contentId: content?.id
          }
        });
        onChangeGroupTabs([defaultContent, ...updatingGroups]);
        onChangeTab(DefaultModuleEnum.Default);
      }
    },
    onError(e) {
      logger.info('e--', e);
    },
  });

  const [deleteSLLGroup, { loading: isRemovingGroup }] = useSllDeleteGroupModuleMutation({
    update(cache, res) {
      if (res.data?.sllDeleteGroupModule) {
        const defaultGroup = groups[0];
        let updatingGroups: any[] = groups.slice(1);
        const index = updatingGroups.findIndex(a => a.groupName === activeTab);
        if (index > -1) {
          updatingGroups.splice(index, 1);
        }
        cache.writeQuery({
          query: GetContentAllGroupsDocument,
          data: {
            getContentAllGroups: {
              __typename: "SLLExtendedModule",
              groups: updatingGroups
            }
          },
          variables: {
            contentId: content?.id
          }
        });
        onChangeGroupTabs([defaultGroup, ...updatingGroups]);
        onChangeTab(DefaultModuleEnum.Default);
      }
    }
  });

  const [deleteSLLGroupContent, { loading: isDeletingGroupContent }] = useSllDeleteGroupModuleContentMutation({
    update(cache, res) {
      if (res.data?.sllDeleteGroupModuleContent) {
        cache.updateFragment({
          id: `SLLGroupModule:{"moduleId":"${currentTabContent?.id}","groupId":"${currentTabContent?.groupId}"} `,
          fragment: SllGroupModuleFragmentDoc,
        }, (c) => ({ ...c, data: null }))
        onChangeData(null);
        const newGroups = [...groups];
        const index = newGroups.findIndex(g => g.groupId === currentTabContent?.groupId);
        newGroups[index].data = null;
        onChangeGroupTabs(newGroups);
      }
    }
  });

  const [createNewGroupContentMutation, { loading: isCreatingGroup }] = useSllCreateGroupModuleMutation({
    update(cache, res) {
      if (res.data?.sllCreateGroupModule && groups.length > 0) {
        let updatingGroups: any[] = groups.slice(1);
        if (updatingGroups.findIndex(u => u.groupId === res.data?.sllCreateGroupModule?.groupId) === -1) {
          updatingGroups = [...updatingGroups, res.data?.sllCreateGroupModule];
        }
        cache.writeQuery({
          query: GetContentAllGroupsDocument,
          data: {
            getContentAllGroups: {
              __typename: "SLLExtendedModule",
              groups: updatingGroups
            }
          },
          variables: {
            moduleId: res.data?.sllCreateGroupModule?.moduleId
          }
        });
      }
    }
  });

  const [updateGroupContent, { loading: isUpdatingGroupContent }] = useSllUpdateGroupModuleMutation({
    update(__cache, res) {
      const updatedGroupContent = res.data?.sllUpdateGroupModule;
      if (updatedGroupContent) {
        let updatingGroups: any[] = [...groups.slice(1)];
        const index = updatingGroups.findIndex(u => u.groupId === updatedGroupContent?.groupId);
        if (index > -1) {
          updatingGroups[index] = {...updatingGroups[index], ...updatedGroupContent, groupName: updatingGroups[index].groupName};
        }
        onChangeGroupTabs([groups[0], ...updatingGroups]);
        clearFile();
      }
    },
    onError(e) {
      logger.debug(`[e]`, e);
    },
  });

  const [updateVideoGroupContent, { loading: isUpdatingGroupVideoContent }] = useSllUpdateVideoGroupModuleMutation({
    update(__cache, res) {
      const d = res.data?.sllUpdateVideoGroupModule;
      if (d) {
        __cache.updateFragment({
          id: `SLLGroupModule:{"moduleId":"${d?.moduleId}","groupId":"${d?.groupId}"} `,
          fragment: SllGroupModuleFragmentDoc,
        }, (c) => ({ ...c, data: d.data, meta: d.meta }))
        if (d?.data) {
          onChangeData(d?.data)
        }
      }
      clearFile();
    },
    onError(e) {
      logger.debug(`[e]`, e);
    },
  });

  const handleRemoveGroup = useCallback(async () => {
    if (window.confirm(`Are you sure you want to remove ${currentTabContent?.groupName} group in the Module?`)) {
      const id = currentTabContent?.id || currentTabContent?.moduleId
      if (id) {
        await deleteGroupContent({
          variables: {
            input: {
              moduleId: id,
              groupId: currentTabContent?.groupId
            }
          }
        })
      }
    }
  }, [currentTabContent, deleteGroupContent]);

  const handleAddGroup = useCallback(() => {
    setOpenAddModal(true);
  }, []);
  const onCloseAddGroupModal = useCallback(() => {
    setOpenAddModal(false)
  }, []);

  return {
    getContentsByGroup,
    createNewGroupContentMutation,
    updateGroupContent,
    deleteGroupModuleData,
    deleteGroupContent,
    deleteSLLGroup,
    deleteSLLGroupContent,
    updateVideoGroupContent,
    isGroupRemoving: isGroupRemoving || deletingGroupModuleData || isRemovingGroup || isDeletingGroupContent,
    isGroupsLoading: isFetchingAllGroups || isCreatingGroup || isUpdatingGroupContent || isUpdatingGroupVideoContent || deletingGroupModuleData,
    handleRemoveGroup,
    isOpenAddModal,
    handleAddGroup,
    onCloseAddGroupModal,
  }
};
